import React, { useState } from 'react';
import { LockIcon, ZapIcon, ShieldCheckIcon, CreditCardIcon, StarIcon, CheckCircle2Icon } from 'lucide-react';
import { Button } from './Button';
type PlanId = 'esencial' | 'profesional' | 'elite';
export function PricingSection() {
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('profesional');
  const plans = {
    esencial: {
      id: 'esencial' as PlanId,
      emoji: '📘',
      name: 'ESENCIAL',
      summary: '3 tomos + 1 bono',
      oldPrice: '$47.000',
      price: '$19.990',
      savings: null,
      ctaText: 'SÍ, QUIERO EL PLAN ESENCIAL →',
      description: 'Incluye Tomo 1 (Manifiesto del Cliente), Tomo 2 (Protocolo de Pedido Infalible), Tomo 3 (Checklist Mensual) y Bono 1 (Kit de Mensajes 3 Niveles).',
      href: 'https://checkout.empezahoy.shop/cart/48176377004264:1',
      details: ['Tomo 1 — Manifiesto del Cliente con contrato de compromiso listo', 'Tomo 2 — Protocolo de Pedido Infalible con scripts para cada situación', 'Tomo 3 — Checklist Mensual por tipo de cliente listo para entregar', 'Bono 1 — Kit de Mensajes 3 Niveles', 'Acceso de por vida + actualizaciones gratuitas'],
      detailsNote: 'La base del sistema para empezar a ordenar tu documentación desde el primer día.'
    },
    profesional: {
      id: 'profesional' as PlanId,
      emoji: '🏆',
      name: 'PROFESIONAL',
      summary: '6 tomos + 3 bonos',
      oldPrice: '$89.000',
      price: '$24.990',
      savings: 'AHORRÁS $64.010',
      badges: ['MÁS ELEGIDO', 'AHORRÁS $64.010'],
      ctaText: 'SÍ, QUIERO EL SISTEMA PRO COMPLETO →',
      description: 'La opción más elegida por contadores argentinos que quieren terminar con la persecución de una vez por todas.',
      href: 'https://checkout.empezahoy.shop/cart/48176373563624:1',
      details: ['Tomo 1 — Manifiesto del Cliente con contrato de compromiso listo', 'Tomo 2 — Protocolo de Pedido Infalible con scripts para cada situación', 'Tomo 3 — Checklist Mensual por tipo de cliente listo para entregar', 'Tomo 4 — El Muro de Excusas con respuestas para cada caso', 'Tomo 5 — Calendario de Gestión con semáforo visual anual', 'Tomo 6 — Onboarding Turbo para que el cliente entre ordenado', 'Bono 1 — Kit de Mensajes 3 Niveles', 'Bono 2 — Tablero A4: Control Total', 'Bono 3 — Guía Express: Reglas de Juego', 'Acceso de por vida + actualizaciones gratuitas'],
      detailsNote: 'La opción más elegida por contadores argentinos que quieren terminar con la persecución de una vez por todas.'
    },
    elite: {
      id: 'elite' as PlanId,
      emoji: '💎',
      name: 'ÉLITE',
      summary: 'Todo lo anterior + Script Cobro + Manual de Delegación',
      oldPrice: '$98.000',
      price: '$31.990',
      savings: null,
      ctaText: 'SÍ, QUIERO EL PLAN ÉLITE →',
      description: 'Para el contador que quiere delegar y escalar su estudio con el Manual de Delegación incluido.',
      href: 'https://checkout.empezahoy.shop/cart/48176378020072:1',
      details: ['Tomo 1 — Manifiesto del Cliente con contrato de compromiso listo', 'Tomo 2 — Protocolo de Pedido Infalible con scripts para cada situación', 'Tomo 3 — Checklist Mensual por tipo de cliente listo para entregar', 'Tomo 4 — El Muro de Excusas con respuestas para cada caso', 'Tomo 5 — Calendario de Gestión con semáforo visual anual', 'Tomo 6 — Onboarding Turbo para que el cliente entre ordenado', 'Bono 1 — Kit de Mensajes 3 Niveles', 'Bono 2 — Tablero A4: Control Total', 'Bono 3 — Guía Express: Reglas de Juego', 'Bono Extra — Script "Cobro de Honorarios Sin Miedo"', 'Manual de Delegación: Tu Primer Asistente Contable', 'Acceso de por vida + actualizaciones gratuitas'],
      detailsNote: 'Para el contador que quiere delegar y escalar su estudio con todas las herramientas incluidas.'
    }
  };
  const currentPlan = plans[selectedPlan];
  return <section id="pricing" className="py-10 bg-[#111827]" data-id="element-116">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-117">
        <div className="text-center mb-6" data-id="element-118">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-2" data-id="element-119">
            Elegí el plan ideal para vos
          </h2>
          <p className="text-sm text-gray-400 font-medium" data-id="element-120">
            Acceso inmediato y de por vida. Pago único, sin suscripciones.
          </p>
        </div>

        <div className="space-y-3 mb-6" data-id="element-121">
          {Object.values(plans).map(plan => {
          const isSelected = selectedPlan === plan.id;
          const isProfesional = plan.id === 'profesional';
          return <div key={plan.id} onClick={() => setSelectedPlan(plan.id)} className={`
                  relative cursor-pointer rounded-xl p-4 border-2 transition-all duration-200
                  ${isSelected ? 'bg-[#1a2333] border-[#d4a017]' : 'bg-[#111827] border-gray-800 hover:border-gray-700'}
                  ${isProfesional && !isSelected ? 'border-gray-700' : ''}
                  ${isProfesional && isSelected ? 'shadow-[0_0_20px_rgba(212,160,23,0.12)]' : ''}
                `} data-id="element-122">
                <div className="flex items-center justify-between" data-id="element-123">
                  <div className="flex items-start gap-4" data-id="element-124">
                    <div className="mt-0.5 flex-shrink-0" data-id="element-125">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#d4a017]' : 'border-gray-500'}`} data-id="element-126">
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#d4a017]" data-id="element-127" />}
                      </div>
                    </div>
                    <div data-id="element-128">
                      <div className="flex items-center gap-1.5 mb-0.5" data-id="element-129">
                        <span className="text-base" data-id="element-130">{plan.emoji}</span>
                        <h3 className="text-base font-black text-white tracking-wide" data-id="element-131">
                          {plan.name}
                        </h3>
                      </div>
                      {isProfesional && <div className="flex flex-wrap gap-1.5 mb-1.5 mt-1" data-id="element-132">
                          <span className="inline-flex items-center gap-1 bg-[#d4a017] text-[#111827] text-[9px] font-black px-1.5 py-0.5 rounded-sm uppercase tracking-wider" data-id="element-133">
                            <StarIcon className="w-2.5 h-2.5 fill-current" data-id="element-134" />
                            MÁS ELEGIDO
                          </span>
                          <span className="inline-flex items-center bg-[#4a3e1c] text-[#d4a017] border border-[#d4a017]/30 text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider" data-id="element-135">
                            AHORRÁS $64.010
                          </span>
                        </div>}
                      <p className="text-xs text-gray-400 font-medium" data-id="element-136">
                        {plan.summary}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3" data-id="element-137">
                    <div className="text-xs text-gray-500 line-through font-bold" data-id="element-138">
                      {plan.oldPrice}
                    </div>
                    <div className="text-xl md:text-2xl font-black text-white whitespace-nowrap" data-id="element-139">
                      {plan.price}
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Dynamic CTA */}
        <div className="mb-3" data-id="element-140">
          <Button href={currentPlan.href} className={`w-full text-sm md:text-base py-3.5 ${selectedPlan === 'profesional' ? 'animate-pulse hover:animate-none' : ''}`} data-id="element-141">
            {currentPlan.ctaText}
          </Button>
        </div>

        {/* Dynamic Description */}
        <p className="text-center text-xs text-gray-400 italic mb-6 px-4" data-id="element-142">
          {currentPlan.description}
        </p>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-5 text-[11px] text-gray-400 font-medium bg-[#1a2333] border border-gray-800 rounded-lg py-3 px-2 mb-8" data-id="element-143">
          <div className="flex items-center" data-id="element-144">
            <LockIcon className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-145" />
            Pago seguro
          </div>
          <div className="flex items-center" data-id="element-146">
            <ZapIcon className="w-4 h-4 text-[#d4a017] mr-1.5" data-id="element-147" />
            Acceso en 5 min
          </div>
          <div className="flex items-center" data-id="element-148">
            <ShieldCheckIcon className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-149" />
            Garantía 7 días
          </div>
          <div className="flex items-center" data-id="element-150">
            <CreditCardIcon className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-151" />
            Mercado Pago
          </div>
        </div>

        {/* Dynamic Plan Details */}
        <div className="bg-[#1a2333] rounded-xl p-5 md:p-6 border border-gray-800" data-id="element-152">
          <h3 className="text-base md:text-lg font-black text-white mb-4 text-center" data-id="element-153">
            El Plan{' '}
            {currentPlan.name.charAt(0) + currentPlan.name.slice(1).toLowerCase()}{' '}
            incluye:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mb-5" data-id="element-154">
            {currentPlan.details.map((detail, index) => <div key={index} className="flex items-start" data-id="element-155">
                <CheckCircle2Icon className="w-4 h-4 text-green-400 mr-2 flex-shrink-0 mt-0.5" data-id="element-156" />
                <span className="text-xs text-gray-300 font-medium" data-id="element-157">
                  {detail}
                </span>
              </div>)}
          </div>
          <div className="text-center" data-id="element-158">
            <p className="text-xs font-bold text-[#d4a017] bg-[#d4a017]/10 inline-block px-4 py-2 rounded-lg border border-[#d4a017]/20" data-id="element-159">
              {currentPlan.detailsNote}
            </p>
          </div>
        </div>
      </div>
    </section>;
}